import { CONVERSATION_ACTIVITY_EVENTS } from '../../../services/messaging-internal-events';
import { MODEL_NAMES } from '../../../constants/index';
import {
  conversationHasExactContacts,
  isSameConversation,
} from '../../../utils/messaging/conversation';
import { filterSystemAndCurrentUser } from '../../../utils/messaging/user';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';

export default class WebsocketActivityHandlersConversation {
  @service messaging;
  @service messagingInternalEvents;
  @service messagingNavigation;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  handleActivity(response) {
    const statusHandlers = {
      updated: payload => this.#handleUpdatedActivity(payload),
      left: payload => this.#handleUpdatedActivity(payload, false),
      read: payload => this.#handleReadActivity(payload),
      replace: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.REPLACE, payload),
      destroyed: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.DESTROYED, payload),
    };
    const handler = response.status ? statusHandlers[response.status] : statusHandlers.updated;
    handler(response.payload);
  }

  #handleUpdatedActivity(payload, showToast = true) {
    // Flag off: do not mutate the in-memory list or store. Refresh still hides
    // inaccessible threads via the index payload.
    if (!this.messaging.groupMessagingEnabled && this.#payloadDisabled(payload)) {
      return;
    }

    if (this.#isUnseenWhileDraftSaving(payload)) {
      return;
    }

    const record = this.#ensureConversationInStore(payload.data.id, payload);

    if (record.disabled) {
      if (this.messaging.groupMessagingEnabled) {
        const eventPayload = {
          conversationId: record.id,
        };
        if (!showToast) {
          eventPayload.showToast = false;
        }
        this.messagingInternalEvents.emit(
          CONVERSATION_ACTIVITY_EVENTS.ACCESS_REVOKED,
          eventPayload
        );
      }
      return;
    }

    if (!this.#conversationInList(this.messaging.conversations, record.id)) {
      this.#addOrReplaceConversationRecord(this.messaging.conversations, record);
    }

    // Broadcasts don't carry live `conflicts`; only show computes them. A client
    // gets a 403 from show on an inactive conversation, and the failed reload
    // sends them back to the list.
    const currentHandler = this.messagingNavigation.currentConversationHandler;
    if (
      isSameConversation(currentHandler?.conversation, record) &&
      (record.inactive || currentHandler.inactiveReasons.length)
    ) {
      currentHandler.reloadFromShow.perform();
    }
  }

  // While an unsaved conversation's `save()` is in flight, the server can
  // broadcast `conversation.updated` before the HTTP POST completes. Pushing
  // that payload creates a second record for the same id before save() assigns
  // the id to the saving record. Skip any unseen update that matches a draft
  // currently saving; `save()` will resolve with the persisted server state.
  #isUnseenWhileDraftSaving(payload) {
    const conversationId = payload.data?.id;
    if (conversationId === null || conversationId === undefined) return false;
    if (this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId)) return false;

    const savingDrafts = this.store
      .peekAll(MODEL_NAMES.CONVERSATION)
      .filter(conversation => conversation.isNew && conversation.isSaving);

    if (!savingDrafts.length) return false;

    return savingDrafts.some(draft => this.#payloadMatchesSavingDraft(payload, draft));
  }

  #payloadMatchesSavingDraft(payload, draft) {
    const included = payload.included ?? [];
    const draftContacts = draft.contacts?.flatMap(c => c.members ?? [c]) ?? [];
    if (!draftContacts.length) {
      return true;
    }

    return draftContacts.every(contact =>
      included.some(
        resource =>
          String(resource.attributes?.external_id) === String(contact.externalId) &&
          String(resource.attributes?.external_scope) === String(contact.externalScope)
      )
    );
  }

  #handleReadActivity(payload) {
    const conversation = this.store.normalize(MODEL_NAMES.CONVERSATION, payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations.content.find(
      conv =>
        conv.isNew &&
        conversationHasExactContacts(conv, persistedConversationUsers, {
          isCouple: persistedConversation.isCouple,
        })
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);
    } else {
      conversations.unshiftObjects([persistedConversation]);
    }
  }

  #conversationInList(conversations, conversationId) {
    return conversations.content.some(conv =>
      conv.isSameId ? conv.isSameId(conversationId) : String(conv.id) === String(conversationId)
    );
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
  }

  #payloadDisabled(payload) {
    return payload.data?.attributes?.disabled === true;
  }
}
